import { AJUDA_PRODUCAO } from "@/lib/ajuda/dialogos/producao";
import { useState } from "react";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Info, Plus } from "lucide-react";
import { toast } from "sonner";
import type { ProsthesisStatus } from "@bookdental/schemas";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { useCustomizationLocked } from "@/lib/use-customization-locked";
import { FluxoStageCard, PontoDeInsercao, type FluxoStage } from "./FluxoStageCard";
import { NovoEstagioDialog } from "./NovoEstagioDialog";

type Raia = "servico" | "modelo";
/** Onde o estágio novo entra. `null` = no fim. */
type Insercao = { raia: Raia; indice: number | null };

/**
 * O fluxo de produção numa tela só: as duas trilhas lado a lado, cada uma na sua ordem, com a
 * mesma mecânica de arrastar do quadro — só que aqui se organiza o FLUXO, não o trabalho dentro
 * dele. É o lugar que faltava: até então os estágios só podiam ser mexidos de dentro do quadro,
 * invisível pra quem trabalha na Planilha.
 */
export function FluxoDeProducaoDialog({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  const utils = trpc.useUtils();
  const travado = useCustomizationLocked();
  const [inserindo, setInserindo] = useState<Insercao | null>(null);

  const stagesQuery = trpc.prosthesis.stages.list.useQuery(undefined, { enabled: aberto });
  const modelQuery = trpc.prosthesis.modelStages.list.useQuery(undefined, { enabled: aberto });
  const stages = (stagesQuery.data ?? []) as FluxoStage[];
  const modelStages = (modelQuery.data ?? []) as FluxoStage[];

  const recarregar = () => {
    utils.prosthesis.stages.list.invalidate();
    utils.prosthesis.modelStages.list.invalidate();
    utils.prosthesis.incoming.list.invalidate();
  };
  const aoFalhar = (e: { message?: string }) => toast.error(e.message || "Não foi possível salvar.");

  const update = trpc.prosthesis.stages.update.useMutation({ onSuccess: recarregar, onError: aoFalhar });
  const reorder = trpc.prosthesis.stages.reorder.useMutation({ onSuccess: recarregar, onError: aoFalhar });
  const remove = trpc.prosthesis.stages.delete.useMutation({
    onSuccess: () => {
      toast.success("Estágio excluído.");
      recarregar();
    },
    onError: aoFalhar,
  });
  const updateModel = trpc.prosthesis.modelStages.update.useMutation({ onSuccess: recarregar, onError: aoFalhar });
  const reorderModel = trpc.prosthesis.modelStages.reorder.useMutation({ onSuccess: recarregar, onError: aoFalhar });
  const removeModel = trpc.prosthesis.modelStages.delete.useMutation({
    onSuccess: () => {
      toast.success("Etapa de modelo excluída.");
      recarregar();
    },
    onError: aoFalhar,
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const aoSoltar = (raia: Raia) => (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const lista = raia === "modelo" ? modelStages : stages;
    const de = lista.findIndex((s) => s.id === e.active.id);
    const para = lista.findIndex((s) => s.id === e.over!.id);
    if (de === -1 || para === -1) return;
    const orderedIds = arrayMove(lista, de, para).map((s) => s.id);
    (raia === "modelo" ? reorderModel : reorder).mutate({ orderedIds });
  };

  /** Criar sempre nasce no fim (é o que a mutation faz); se o pedido foi "inserir aqui",
   *  reordena logo em seguida pra levar o novo até a posição escolhida. */
  const aoCriar = (novoId: string) => {
    if (!inserindo) return;
    const { raia, indice } = inserindo;
    if (indice === null) return;
    const lista = raia === "modelo" ? modelStages : stages;
    const ids = [...lista.map((s) => s.id)];
    ids.splice(indice, 0, novoId);
    (raia === "modelo" ? reorderModel : reorder).mutate({ orderedIds: ids });
  };

  const raiaUI = (
    raia: Raia,
    titulo: string,
    descricao: string,
    lista: FluxoStage[],
    permiteDestino: boolean,
    raiaLabel: string,
    acoes: {
      renomear: (id: string, label: string) => void;
      cor: (id: string, colorHex: string) => void;
      destino: (id: string, valor: ProsthesisStatus | null) => void;
      excluir: (s: FluxoStage) => void;
    },
  ) => (
    <section className="flex min-w-0 flex-col gap-2">
      <div>
        <h3 className="text-sm font-bold text-zinc-900 dark:text-foreground">{titulo}</h3>
        <p className="text-xs text-zinc-500 dark:text-muted-foreground">{descricao}</p>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={aoSoltar(raia)}>
        <SortableContext items={lista.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <ul className="flex flex-col gap-1 rounded-lg border border-zinc-200 p-2 dark:border-border">
            {lista.length === 0 && (
              <li className="py-5 text-center text-xs text-zinc-500 dark:text-muted-foreground">
                Nenhuma etapa criada ainda.
              </li>
            )}
            {lista.map((s, i) => (
              <FluxoStageCardComInsercao
                key={s.id}
                stage={s}
                posicao={i}
                travado={travado}
                permiteDestino={permiteDestino}
                raiaLabel={raiaLabel}
                onInserirAntes={() => setInserindo({ raia, indice: i })}
                acoes={acoes}
              />
            ))}
            {!travado && lista.length > 0 && (
              <PontoDeInsercao
                onInserir={() => setInserindo({ raia, indice: lista.length })}
                rotulo={`Inserir no fim (${raiaLabel})`}
              />
            )}
          </ul>
        </SortableContext>
      </DndContext>

      {!travado && (
        <button
          type="button"
          onClick={() => setInserindo({ raia, indice: null })}
          className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-dashed border-zinc-300 px-3 text-xs font-semibold text-zinc-600 transition-colors hover:border-brand-300 hover:text-brand-700 dark:border-border dark:text-muted-foreground"
        >
          <Plus className="h-3.5 w-3.5" />
          {raia === "modelo" ? "Nova etapa de modelo" : "Novo estágio"}
        </button>
      )}
    </section>
  );

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="flex max-h-[90dvh] max-w-3xl flex-col gap-4 overflow-y-auto border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle className="dark:text-foreground" ajuda={AJUDA_PRODUCAO.fluxoDeProducao}>Fluxo de produção</DialogTitle>
          <DialogDescription className="dark:text-muted-foreground">
            As duas trilhas do laboratório, lado a lado. Arraste pela alça pra reordenar.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-5 md:grid-cols-2">
          {raiaUI(
            "servico",
            "Estágios do serviço",
            "O serviço percorre estes estágios, na ordem. Mover um serviço de estágio nunca muda o status dele.",
            stages,
            true,
            "estágio do serviço",
            {
              renomear: (id, label) => update.mutate({ id, label }),
              cor: (id, colorHex) => update.mutate({ id, colorHex }),
              destino: (id, completionBehavior) => update.mutate({ id, completionBehavior }),
              excluir: (s) => {
                if (window.confirm(`Excluir o estágio "${s.label}"? Os serviços nele voltam pra "Sem estágio".`)) {
                  remove.mutate({ id: s.id });
                }
              },
            },
          )}

          {raiaUI(
            "modelo",
            "Etapas do modelo",
            "Trilha paralela da fabricação do modelo físico — anda por conta própria e nunca influencia o status do serviço.",
            modelStages,
            false,
            "etapa do modelo",
            {
              renomear: (id, label) => updateModel.mutate({ id, label }),
              cor: (id, colorHex) => updateModel.mutate({ id, colorHex }),
              destino: () => undefined,
              excluir: (s) => {
                if (window.confirm(`Excluir a etapa "${s.label}"? Os serviços nela voltam pra "Não possui".`)) {
                  removeModel.mutate({ id: s.id });
                }
              },
            },
          )}
        </div>

        {travado && (
          <p className="flex items-start gap-2 rounded-lg border border-dashed border-zinc-200 px-3 py-2.5 text-xs text-zinc-500 dark:border-border dark:text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Personalizar o fluxo — criar, renomear, recolorir e reordenar etapas — faz parte do plano pago.
          </p>
        )}

        <NovoEstagioDialog
          aberto={!!inserindo}
          modelo={inserindo?.raia === "modelo"}
          onFechar={() => setInserindo(null)}
          onCriado={(estagio) => aoCriar(estagio.id)}
        />
      </DialogContent>
    </Dialog>
  );
}

/** Ponto de inserção + cartão, na ordem — separado só pra não aninhar demais o `raiaUI`. */
function FluxoStageCardComInsercao({
  stage,
  posicao,
  travado,
  permiteDestino,
  raiaLabel,
  onInserirAntes,
  acoes,
}: {
  stage: FluxoStage;
  posicao: number;
  travado: boolean;
  permiteDestino: boolean;
  raiaLabel: string;
  onInserirAntes: () => void;
  acoes: {
    renomear: (id: string, label: string) => void;
    cor: (id: string, colorHex: string) => void;
    destino: (id: string, valor: ProsthesisStatus | null) => void;
    excluir: (s: FluxoStage) => void;
  };
}) {
  return (
    <>
      {!travado && (
        <PontoDeInsercao onInserir={onInserirAntes} rotulo={`Inserir antes de ${stage.label} (${raiaLabel})`} />
      )}
      <FluxoStageCard
        stage={stage}
        posicao={posicao}
        travado={travado}
        permiteDestino={permiteDestino}
        raiaLabel={raiaLabel}
        onRenomear={(label) => acoes.renomear(stage.id, label)}
        onCor={(c) => acoes.cor(stage.id, c)}
        onDestino={(v) => acoes.destino(stage.id, v)}
        onExcluir={() => acoes.excluir(stage)}
      />
    </>
  );
}
